export type AdminSupplierSummary = {
  id: number;
  name: string;
  contact: string;
  catalogUrls: string[];
  addedOn: string | null;
  linkedProducts: number;
};

export type AdminSupplierDetail = AdminSupplierSummary & {
  links: Array<{
    id: number;
    inventoryId: number | null;
    inventoryName: string;
    inventorySlug: string | null;
    inventoryQuantity: number;
    supplierProductName: string;
    sku: string;
    price: number;
    quantity: number;
    addedOn: string | null;
  }>;
};

export type AdminSuppliersResponse = {
  suppliers: AdminSupplierSummary[];
  pagination: {
    page: number;
    pageSize: number;
    totalSuppliers: number;
    totalPages: number;
    hasPreviousPage: boolean;
    hasNextPage: boolean;
  };
  summary: {
    totalSuppliers: number;
    mappedSuppliers: number;
    catalogCount: number;
  };
};

export type AdminSupplierInput = {
  name: string;
  contact?: string;
  catalogUrls?: string[];
};

export type AdminSupplierLinkInput = {
  inventoryId: number;
  name: string;
  sku: string;
  price: number;
  quantity: number;
};

async function parseResponse(response: Response) {
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(data?.message || `Request failed: ${response.status}`);
  }

  return data;
}

export async function fetchAdminSuppliers(query: URLSearchParams) {
  const response = await fetch(`/api/admin/suppliers?${query.toString()}`, {
    credentials: "include",
    headers: { Accept: "application/json" }
  });

  return (await parseResponse(response)) as AdminSuppliersResponse;
}

export async function fetchAdminSupplier(supplierId: number) {
  const response = await fetch(`/api/admin/suppliers/${supplierId}`, {
    credentials: "include",
    headers: { Accept: "application/json" }
  });

  return (await parseResponse(response)) as AdminSupplierDetail;
}

export async function createAdminSupplier(input: AdminSupplierInput) {
  const response = await fetch("/api/admin/suppliers", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(input)
  });

  return parseResponse(response);
}

export async function updateAdminSupplier(supplierId: number, input: AdminSupplierInput) {
  const response = await fetch(`/api/admin/suppliers/${supplierId}`, {
    method: "PUT",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(input)
  });

  return parseResponse(response);
}

export async function createAdminSupplierLink(supplierId: number, input: AdminSupplierLinkInput) {
  const response = await fetch(`/api/admin/suppliers/${supplierId}/links`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(input)
  });

  return parseResponse(response);
}

export async function deleteAdminSupplierLink(linkId: number) {
  const response = await fetch(`/api/admin/suppliers/links/${linkId}`, {
    method: "DELETE",
    credentials: "include",
    headers: { Accept: "application/json" }
  });

  return parseResponse(response);
}
